<template>
  <div class="interface-area">
    <WindowModal :title="modalTitle" type="Global">
      <template #modal-content>
        <ConfirmSwap
          ref="confirmSwap"
          :swap="swap"
          input-method="exchange"
          :transaction="transaction"
          @swapTokens="exchangeTokens"
          @initConfirmState="changeModalTitle('init')"
          @initLoading="changeModalTitle('loading')"
          @initSuccess="changeModalTitle('success')"
          @initError="changeModalTitle('error')"
          @closeGlobalModal="closeModal"
          @priceRefresh="loadBalances"
        />
      </template>
    </WindowModal>
    <div class="swap-box">
      <div class="header-area">
        <h1 class="smaller">
          {{ $t('exchange.h1') }}:
        </h1>
      </div>
      <div class="tokens-area">
        <div class="token-item">
          <div class="form-area">
            <div class="input-area">
              <label for="inputFrom">
                {{ $t('swap.from') }}
              </label>
              <input
                id="inputFrom"
                v-model="swap.from_value"
                type="number"
                min="0"
                step="0.0001"
                lang="en"
                placeholder="0.0..."
                @input="inputFrom"
              >
              <div :class="{'max-button': true, 'active': swap.from_token.balance !== undefined && parseFloat(swap.from_token.balance) > 0}" @click="setFromMax">
                <p class="text">
                  {{ $t('swap.max') }}
                </p>
              </div>
            </div>
            <div class="token-select non-interactive">
              <p class="desc">
                {{ swap.from_token.name }}
              </p>
              <div class="token-data">
                <div class="token-area">
                  <nuxt-img
                    :src="'/img/new/' + swap.from_token.name.toLowerCase() + '_item.svg'"
                    placeholder
                    :lazy="true"
                  />
                  <p>
                    {{ swap.from_token.symbol }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <p v-if="swap.from_token.balance !== undefined" class="available">
            {{ $t('swap.available') }}:
            <span v-if="swap.from_token.balance !== null" class="bold">{{ $formatCryptoAmount(swap.from_token.balance, 4) }}</span>
            <span v-else class="bold">0</span>
          </p>
        </div>
        <div class="swap-area">
          <div class="swap-button" @click="swapInputs">
            <Swap />
          </div>
        </div>
        <div class="token-item">
          <div class="form-area">
            <div class="input-area">
              <label for="inputFrom">
                {{ $t('swap.to') }}
              </label>
              <input
                id="inputFrom"
                v-model="swap.to_value"
                type="number"
                min="0"
                step="0.0001"
                lang="en"
                placeholder="0.0..."
                @input="inputTo"
              >
              <div :class="{'max-button': true, 'active': swap.to_token.balance !== undefined && parseFloat(swap.to_token.balance) > 0}" @click="setToMax">
                <p class="text">
                  {{ $t('swap.max') }}
                </p>
              </div>
            </div>
            <div class="token-select non-interactive">
              <p class="desc">
                {{ swap.to_token.name }}
              </p>
              <div class="token-data">
                <div class="token-area">
                  <nuxt-img
                    :src="'/img/new/' + swap.to_token.name.toLowerCase() + '_item.svg'"
                    placeholder
                    :lazy="true"
                  />
                  <p>
                    {{ swap.to_token.symbol }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <p v-if="swap.to_token.balance !== undefined" class="available">
            {{ $t('swap.available') }}:
            <span v-if="swap.to_token.balance !== null" class="bold">{{ $formatCryptoAmount(swap.to_token.balance, 4) }}</span>
            <span v-else class="bold">0</span>
          </p>
        </div>
      </div>
      <div :class="{'warning-area': true, 'active': errorText.length > 0}">
        <div class="icon-area">
          <Warn />
        </div>
        <div class="text-area">
          {{ errorText }}
        </div>
      </div>
      <div :class="{'info-area': true, 'active': floatieDelayActive}">
        <div class="icon-area">
          <Info />
        </div>
        <div class="text-area">
          {{ $t('exchange.delay', { token: '$FLE', fee: swap.contract.fee }) }}
          <span class="bold">
            {{ calculateDate(swap.contract.floatieDelay + swap.contract.scheduleFloatieDelay) }}
          </span>
        </div>
      </div>
      <div :class="{'info-area': true, 'active': splashieDelayActive}">
        <div class="icon-area">
          <Info />
        </div>
        <div class="text-area">
          {{ $t('exchange.delay', { token: '$PHE', fee: swap.contract.fee }) }}
          <span class="bold">
            {{ calculateDate(swap.contract.splashieDelay + swap.contract.scheduleSplashieDelay) }}
          </span>
        </div>
      </div>
      <div class="buttons-area">
        <button
          v-if="user !== undefined && user !== null"
          type="button"
          :class="{ 'btn cyan': true, 'disabled': !tokensPicked || loading || swapInsufficient }"
          :disabled="!tokensPicked || loading || swapInsufficient"
          @click="prepareSwap"
        >
          <span v-if="!loading">
            {{ swapCta }}
          </span>
          <span v-else>
            <Loading />
          </span>
        </button>
        <button v-else class="btn cyan" @click="openConnectModal">
          {{ $t('common.connect_wallet') }}
        </button>
      </div>
    </div>
    <div class="cyan-cloud">
      <CloudCyanLiquidityLeft />
    </div>
    <div class="cyan-cloud2">
      <CloudCyanLiquidityRight />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  hydrateWhenIdle,
  hydrateWhenVisible
} from 'vue-lazy-hydration'

import ConfirmSwap from '~/components/app/interface/ConfirmSwap'
import Swap from '~/components/svg/new/Swap'

export default {
  name: 'RewardExchangeComponent',
  components: {
    CloudCyanLiquidityLeft: hydrateWhenIdle(() => import('~/components/svg/new/clouds/CloudCyanLiquidityLeft')),
    CloudCyanLiquidityRight: hydrateWhenIdle(() => import('~/components/svg/new/clouds/CloudCyanLiquidityRight')),
    ConfirmSwap,
    Loading: hydrateWhenVisible(() => import('~/components/svg/new/Loading')),
    Info: hydrateWhenVisible(() => import('~/components/svg/new/Info')),
    Swap,
    Warn: hydrateWhenVisible(() => import('~/components/svg/new/Warn')),
    WindowModal: hydrateWhenIdle(() => import('~/components/modals/WindowModal'))
  },
  layout: 'new',
  data() {
    return {
      walletConnected: false,
      modalTitle: this.$t('swap.confirm_swap'),
      currentAccount: this.$t('common.connect_wallet'),
      errorText: '',
      swap: {
        from_value: null,
        from_token: {
          name: this.$config.floatieName,
          symbol: this.$config.floatieSymbol,
          address: this.$config.periodToken,
          decimals: 18
        },
        to_value: null,
        to_token: {
          name: this.$config.splashieName,
          symbol: this.$config.splashieSymbol,
          address: this.$config.periodReset,
          decimals: 18
        },
        contract: {
          floatie: 0,
          splashie: 0,
          fee: 10,
          rate: 20,
          scheduleSplashieDelay: 0,
          scheduleFloatieDelay: 0,
          floatieDelay: 0,
          splashieDelay: 0,
          loadedFromBlockchain: false
        }
      },
      transaction: {
        gasFee: 3000000,
        tx: '',
        loading: false,
        pending: false
      },
      inputMethod: 'from',
      swapCta: this.$t('exchange.cta'),
      swapInsufficient: false,
      loading: false
    }
  },
  async fetch({ store, error, route }) {
    try {
      const pageName = route.name.split('___')
      const seoData = store.getters['seos/seosByUri'](pageName[0])
      if (seoData === null) {
        await store.dispatch('seos/fetchSeos', {
          uri: route.name
        })
      }
    } catch (err) {
      error(err)
    }
  },
  head({ $config: { appUrl } }) {
    return {
      title: this.meta ? this.meta.title : this.$t('common.launching'),
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-description',
          name: 'og:description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-title',
          name: 'og:title',
          content: this.meta
            ? this.meta.title
            : this.$t('common.launching')
        },
        {
          hid: 'og-image',
          name: 'og:image',
          content: this.meta
            ? this.meta.image
            : appUrl + 'seo/default.jpg'
        },
        {
          hid: 'keywords',
          name: 'keywords',
          content: this.meta ? this.meta.keyword : ''
        }
      ]
    }
  },
  computed: {
    ...mapGetters('auth', [
      'user'
    ]),
    ...mapGetters('seos', [
      'seosByUri'
    ]),
    meta() {
      const pageName = this.$nuxt.$route.name.split('___')
      return this.seosByUri(pageName[0])
    },
    tokensPicked() {
      return this.swap.from_value !== null && this.swap.to_value !== null &&
        this.swap.from_value > 0 && this.swap.to_value > 0 && this.walletConnected
    },
    floatieDelayActive() {
      return this.swap.from_token.address === this.$config.periodToken &&
      Date.now() / 1000 < this.swap.contract.floatieDelay + this.swap.contract.scheduleFloatieDelay &&
      this.swap.contract.loadedFromBlockchain
    },
    splashieDelayActive() {
      return this.swap.from_token.address === this.$config.periodReset &&
      Date.now() / 1000 < this.swap.contract.splashieDelay + this.swap.contract.scheduleSplashieDelay &&
      this.swap.contract.loadedFromBlockchain
    }
  },
  created() {
    this.$nuxt.$on('loginWallet', this.loadExchangeData)
  },
  mounted() {
    if (this.user !== undefined && this.user !== null) {
      this.currentAccount = this.$t('common.connected_as') + ' ' + this.$shortenAddress(this.user.address)
      this.walletConnected = true
      this.loadExchangeData()
    }
    if (this.$nuxt.$route.path === '/rewards' && this.user !== null) {
      this.walletConnected = true
      this.currentAccount = this.$t('common.connected_as') + ' ' + this.$shortenAddress(this.user.address)
      window.ethereum.on('accountsChanged', this.loadExchangeData)
    }
  },
  methods: {
    ...mapActions('modal', [
      'changeModalGlobalState',
      'changeModalWalletState'
    ]),
    ...mapActions('tokens', [
      'addToken'
    ]),
    openConnectModal() {
      this.changeModalWalletState({
        state: true
      })
    },
    async loadBalances() {
      const chainId = await window.eth.eth.net.getId()
      if (!this.$isChainSupported(chainId)) {
        this.loading = false
        return
      }
      try {
        this.loading = true
        const userAddress = await this.$getUserAddress()
        const ERC20File = await import('~/assets/abis/ERC20.json')
        const ERC20 = ERC20File.default
        const tokenContractFrom = new window.eth.eth.Contract(ERC20, this.swap.from_token.address)
        const balanceFloatie = await tokenContractFrom.methods.balanceOf(this.$config.periodExchange).call({ from: userAddress })
        this.swap.contract.floatie = parseFloat(window.eth.utils.fromWei(balanceFloatie))
        if (this.user !== undefined && this.user !== null) {
          const balanceFrom = await tokenContractFrom.methods.balanceOf(userAddress).call({ from: userAddress })
          this.swap.from_token.balance = parseFloat(window.eth.utils.fromWei(balanceFrom))
        }
        const tokenContractTo = new window.eth.eth.Contract(ERC20, this.swap.to_token.address)
        const balanceSplashie = await tokenContractTo.methods.balanceOf(this.$config.periodExchange).call({ from: userAddress })
        this.swap.contract.splashie = parseFloat(window.eth.utils.fromWei(balanceSplashie))
        if (this.user !== undefined && this.user !== null) {
          const balanceTo = await tokenContractTo.methods.balanceOf(userAddress).call({ from: userAddress })
          this.swap.to_token.balance = parseFloat(window.eth.utils.fromWei(balanceTo))
        }
        this.loading = false
      } catch (err) {
        console.log(err)
        this.loading = false
      }
    },
    async loadContractData() {
      const chainId = await window.eth.eth.net.getId()
      if (!this.$isChainSupported(chainId)) {
        this.loading = false
        return
      }
      try {
        this.loading = true
        const userAddress = await this.$getUserAddress()
        const PeriodExchangeFile = await import('~/assets/abis/PeriodExchange.json')
        const PeriodExchange = PeriodExchangeFile.default
        const contract = new window.eth.eth.Contract(PeriodExchange, this.$config.periodExchange)

        const fee = await contract.methods.fee().call({ from: userAddress })
        const rate = await contract.methods.rate().call({ from: userAddress })
        const scheduleFloatieDelay = await contract.methods.scheduleFloatieDelay().call({ from: userAddress })
        const scheduleSplashieDelay = await contract.methods.scheduleSplashieDelay().call({ from: userAddress })
        const scheduleForSplashie = await contract.methods.scheduleForSplashie(userAddress).call({ from: userAddress })
        const scheduleForFloatie = await contract.methods.scheduleForFloatie(userAddress).call({ from: userAddress })

        this.swap.contract.rate = parseInt(rate, 10) / 100
        this.swap.contract.fee = parseInt(fee, 10) / 100
        this.swap.contract.scheduleFloatieDelay = parseInt(scheduleFloatieDelay, 10)
        this.swap.contract.scheduleSplashieDelay = parseInt(scheduleSplashieDelay, 10)
        this.swap.contract.splashieDelay = parseInt(scheduleForSplashie, 10)
        this.swap.contract.floatieDelay = parseInt(scheduleForFloatie, 10)
        this.swap.contract.loadedFromBlockchain = true
        this.loading = false
      } catch (err) {
        console.log(err)
        this.loading = false
      }
    },
    inputFrom() {
      if (this.swap.from_token.address === this.$config.periodToken) {
        const toValue = this.swap.from_value * this.swap.contract.rate
        const delayEnd = this.swap.contract.scheduleFloatieDelay + this.swap.contract.floatieDelay
        this.swap.to_value = Date.now() / 1000 > delayEnd ? toValue : (toValue - toValue / 100 * this.swap.contract.fee)
      } else {
        const toValue = this.swap.from_value / this.swap.contract.rate
        const delayEnd = this.swap.contract.scheduleSplashieDelay + this.swap.contract.splashieDelay
        this.swap.to_value = Date.now() / 1000 > delayEnd ? toValue : (toValue - toValue / 100 * this.swap.contract.fee)
      }
      this.checkInsufficientFunds()
    },
    inputTo() {
      if (this.swap.to_token.address === this.$config.periodReset) {
        const fromValue = this.swap.to_value / this.swap.contract.rate
        const delayEnd = this.swap.contract.scheduleFloatieDelay + this.swap.contract.floatieDelay
        this.swap.from_value = Date.now() / 1000 > delayEnd ? fromValue : (fromValue + fromValue / 100 * this.swap.contract.fee)
      } else {
        const fromValue = this.swap.to_value * this.swap.contract.rate
        const delayEnd = this.swap.contract.scheduleSplashieDelay + this.swap.contract.splashieDelay
        this.swap.from_value = Date.now() / 1000 > delayEnd ? fromValue : (fromValue + fromValue / 100 * this.swap.contract.fee)
      }
      this.checkInsufficientFunds()
    },
    clearForm() {
      this.swap.from_value = null
      this.swap.to_value = null
    },
    swapInputs() {
      const fromValue = this.swap.from_value
      const fromToken = this.swap.from_token
      const toValue = this.swap.to_token.address === this.$config.periodReset ? this.swap.to_value / this.swap.contract.rate : this.swap.to_value * this.swap.contract.rate
      const delayEnd = this.swap.to_token.address === this.$config.periodReset ? this.swap.contract.scheduleFloatieDelay + this.swap.contract.floatieDelay : this.swap.contract.scheduleSplashieDelay + this.swap.contract.splashieDelay
      this.swap = {
        from_token: this.swap.to_token,
        to_value: fromValue,
        to_token: fromToken,
        contract: this.swap.contract
      }
      this.swap.from_value = Date.now() / 1000 > delayEnd ? toValue : (toValue + toValue / 100 * this.swap.contract.fee)
      this.inputFrom()
    },
    setFromMax() {
      this.swap.from_value = this.swap.from_token.balance
      this.inputFrom()
    },
    setToMax() {
      this.swap.to_value = this.swap.to_token.balance
      this.inputTo()
    },
    async prepareSwap() {
      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        return
      }
      if (this.tokensPicked) {
        this.$store.dispatch('modal/changeModalGlobalState', {
          state: true
        })
      }
    },
    async exchangeTokens() {
      const userAddress = await this.$getUserAddress()
      const PeriodExchangeFile = await import('~/assets/abis/PeriodExchange.json')
      const PeriodExchange = PeriodExchangeFile.default
      const contract = new window.eth.eth.Contract(PeriodExchange, this.$config.periodExchange)
      const method = this.swap.from_token.address === this.$config.periodToken ? 'exchangeFloatie' : 'exchangeSplashie'
      try {
        this.transaction.pending = true
        const swapValue = window.eth.utils.toBN(
          window.eth.utils.toWei(
            Number(parseFloat(this.swap.from_value)).toPrecision(10)
          )
        )
        const gasPrice = await this.$calculateGasCost()
        const gasFeeEstimated = await contract.methods[method](swapValue)
          .estimateGas({ from: userAddress, gas: this.transaction.gasFee })
        await contract.methods[method](swapValue)
          .send({ from: userAddress, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
          .then((result) => {
            this.transaction.loading = false
            this.transaction.pending = false
            this.transaction.tx = result.transactionHash
            if (this.$refs.confirmSwap !== undefined) {
              this.$refs.confirmSwap.initSuccessScreen()
            }
            this.$saveTransaction({
              tid0: this.swap.from_token.address,
              tid1: this.swap.to_token.address,
              tid0_token: this.swap.from_token,
              tid1_token: this.swap.to_token,
              t0_amount: this.swap.from_value,
              t1_amount: this.swap.to_value,
              hash: result.transactionHash,
              gasUsed: result.gasUsed,
              transactionIndex: result.transactionIndex,
              type: 'exchange'
            },
            this.$t('common.swap_action') + ' ' + this.$preciseTokenValue(this.swap.from_value) + ' ' + this.swap.from_token.symbol.toUpperCase() +
              ' ' + this.$t('common.for') + ' ' + this.$preciseTokenValue(this.swap.to_value) + ' ' + this.swap.to_token.symbol.toUpperCase()
            )
            this.clearForm()
          })
          .catch((err) => {
            this.transaction.pending = false
            this.transaction.loading = false
            if (err.code === 4001) {
              if (this.$refs.confirmSwap !== undefined) {
                this.$refs.confirmSwap.rejectTransaction({
                  type: 'denied'
                })
              }
            } else {
              const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
              if (this.$refs.confirmSwap !== undefined) {
                this.$refs.confirmSwap.rejectTransaction({
                  type: 'error',
                  message: errorMessage
                })
              }
            }
          })
      } catch (err) {
        this.transaction.pending = false
        this.transaction.loading = false
        let errorMessage = this.$t('swap.error_text')
        if (err.message !== undefined && err.message !== null) {
          errorMessage = this.$t('swap.error_text_with_error', { error: this.$getRPCError(err.message) })
        }
        if (this.$refs.confirmSwap !== undefined) {
          this.$refs.confirmSwap.rejectTransaction({
            type: 'error',
            message: errorMessage
          })
        }
      }
    },
    checkInsufficientFunds() {
      if (this.user !== undefined && this.user !== null) {
        if (this.swap.from_value !== null && parseFloat(this.swap.from_value) === 0 && this.swap.to_value !== null) {
          this.errorText = this.$t('swap.insufficient', { symbol: this.swap.from_token.symbol.toUpperCase() })
          this.swapInsufficient = true
        } else if (this.swap.to_value !== null && parseFloat(this.swap.to_value) === 0 && this.swap.from_value !== null) {
          this.errorText = this.$t('swap.insufficient', { symbol: this.swap.to_token.symbol.toUpperCase() })
          this.swapInsufficient = true
        } else {
          this.errorText = ''
          this.swapInsufficient = false
        }
        const tokensBalance = this.swap.to_token.address === this.$config.periodReset ? this.swap.contract.splashie : this.swap.contract.floatie
        if (this.swap.to_value !== null && parseFloat(this.swap.to_value) > tokensBalance) {
          this.errorText = this.$t('exchange.insufficient', { token: this.swap.to_token.symbol.toUpperCase() })
          this.swapInsufficient = true
        }
      }
    },
    changeModalTitle(titleType) {
      switch (titleType) {
        case 'loading':
          this.modalTitle = this.$t('swap.waiting_for_confirmation')
          break
        case 'success':
          this.modalTitle = this.$t('swap.success')
          break
        case 'error':
          this.modalTitle = this.$t('swap.error_header')
          break
        default:
          this.modalTitle = this.$t('swap.confirm_swap')
      }
    },
    async loadExchangeData() {
      const chainId = await window.eth.eth.net.getId()
      if (this.user !== undefined && this.user !== null && this.$isChainSupported(chainId)) {
        await this.loadBalances()
        this.loadContractData()
      }
    },
    calculateDate(newTimestamp) {
      return this.$beautifyDate(newTimestamp * 1000)
    },
    closeModal() {
      this.changeModalGlobalState({
        state: false
      })
    }
  }
}
</script>

<style scoped>
  @import url('../assets/css/new/swap.css');
  @import url('../assets/css/new/tokenSelectorWide.css');
</style>
